import axios from "axios";
import { MKS_BACKEND_URL } from "src/utils/env";
import { GlobalUtils } from "src/utils/globalUtils";
import type {
  MarriageProfileSearchParams,
  MarriageProfileVerifyExistsRequest,
  MarriageProfileVerifyExistsResponse,
} from "src/types/marriageProfileService.types";

const BASE_URL = MKS_BACKEND_URL;

// Uses the same localStorage key set by GlobalUtils.setUserLoginInfo
const getLoggedInUserEmailOrThrow = (): string => {
  const info = GlobalUtils.getUserLoginInfo();
  const email = info?.emailId;
  if (!email) throw new Error("Missing logged-in user email");
  return email;
};

export type MarriageProfilePatchData = Record<string, unknown>;

export const MarriageProfileService = {
  // Convenience for the common case: logged-in user creates a profile (for self or managed by self).
  async createForLoggedInUser(opts: { data: unknown; photo?: File | null; additionalPhotos?: (File | null)[] }) {
    const email = getLoggedInUserEmailOrThrow();
    return this.create(email, opts);
  },

  async getById(id: string | number) {
    const token = GlobalUtils.getToken();
    const res = await axios.get(`${BASE_URL}/marriage-profiles`, {
      params: { id },
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  },

  async search(yourProfileId: string | number, params: MarriageProfileSearchParams) {
    const token = GlobalUtils.getToken();
    const res = await axios.get(
      `${BASE_URL}/marriage-profiles/search/${encodeURIComponent(String(yourProfileId))}`,
      {
        params,
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return res.data;
  },

  async verifyExists(params: MarriageProfileVerifyExistsRequest) {
    const token = GlobalUtils.getToken();
    // NOTE: Backend currently defines `@GetMapping("/verify-exists")` *with* `@RequestBody`,
    // which most HTTP clients/browsers won't send reliably.
    // Keeping as POST on the frontend is usually correct; update once backend contract is finalized.
    const res = await axios.post(`${BASE_URL}/marriage-profiles/verify-exists`, params, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data as MarriageProfileVerifyExistsResponse;
  },

  async create(userEmail: string, opts: { data: unknown; photo?: File | null; additionalPhotos?: (File | null)[] }) {
    const token = GlobalUtils.getToken();

    const form = new FormData();
    // Important: Spring binds `@RequestPart("data") MarriageProfileDTO dto` using JSON converters.
    // Send this part as application/json (Postman does: type=application/json).
    form.append("data", new Blob([JSON.stringify(opts.data)], { type: "application/json" }));
    if (opts.photo) form.append("photo", opts.photo);
    // Append each additional photo as "additionalPhotos" multipart part
    if (opts.additionalPhotos) {
      for (const f of opts.additionalPhotos) {
        if (f) form.append("additionalPhotos", f);
      }
    }

    const res = await axios.post(
      `${BASE_URL}/marriage-profiles/${encodeURIComponent(userEmail)}`,
      form,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
          "X-CHANGE-REASON": "newly created",
          // Don't set Content-Type; axios/browser will set multipart/form-data with boundary.
        },
      }
    );

    // Return full response so UI can inspect HTTP status when needed.
    return res;
  },

  async updatePut(id: string | number, opts: { data: unknown; photo?: File | null }) {
    const token = GlobalUtils.getToken();

    const form = new FormData();
    form.append("data", new Blob([JSON.stringify(opts.data)], { type: "application/json" }));
    if (opts.photo) form.append("photo", opts.photo);

    const res = await axios.put(
      `${BASE_URL}/marriage-profiles/${encodeURIComponent(String(id))}`,
      form,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
          // Don't set Content-Type; axios/browser will set multipart/form-data with boundary.
        },
      }
    );
    return res.data;
  },

  async updatePatch(
    id: string | number,
    opts: {
      data: MarriageProfilePatchData;
      photo?: File | null;
      additionalPhotos?: (File | null)[];
      changeReason?: string;
    },
  ) {
    const token = GlobalUtils.getToken();

    const form = new FormData();
    form.append("data", new Blob([JSON.stringify(opts.data)], { type: "application/json" }));
    if (opts.photo) form.append("photo", opts.photo);
    // Append each additional photo as "additionalPhotos" multipart part
    if (opts.additionalPhotos) {
      for (const f of opts.additionalPhotos) {
        if (f) form.append("additionalPhotos", f);
      }
    }

    const headers: Record<string, string> = {
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
      // Don't set Content-Type; axios/browser will set multipart/form-data with boundary.
    };

    if (opts.changeReason) {
      headers["X-CHANGE-REASON"] = opts.changeReason;
    }

    const res = await axios.patch(
      `${BASE_URL}/marriage-profiles/${encodeURIComponent(String(id))}`,
      form,
      { headers },
    );

    // Return full response so UI can inspect HTTP status when needed.
    return res;
  },

  async checkMutualConnection(requestProfileId: string | number, yourProfileId: string | number) {
    const token = GlobalUtils.getToken();
    const res = await axios.get(
      `${BASE_URL}/marriage-profiles/view`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
        params: {
          requestProfileId,
          yourProfileId,
        },
      }
    );
    return res.data;
  },

  /**
   * Upload up to 4 additional images for a marriage profile.
   * POST /marriage-profiles/{profileId}/images  (multipart/form-data, key: files)
   */
  async uploadImages(profileId: string | number, files: File[], changeReason?: string) {
    const token = GlobalUtils.getToken();
    const form = new FormData();
    for (const f of files) {
      form.append("files", f);
    }

    const headers: Record<string, string> = {
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
    };
    if (changeReason) {
      headers["X-CHANGE-REASON"] = changeReason;
    }

    const res = await axios.post(
      `${BASE_URL}/marriage-profiles/${encodeURIComponent(String(profileId))}/images`,
      form,
      { headers },
    );

    return res;
  },

  /**
   * Delete a marriage profile entirely.
   * DELETE /marriage-profiles?id={profileId}
   */
  async deleteProfile(profileId: string | number) {
    const token = GlobalUtils.getToken();
    const res = await axios.delete(`${BASE_URL}/marriage-profiles/${encodeURIComponent(String(profileId))}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res;
  },

  /**
   * Delete a single additional image from a marriage profile.
   * DELETE /marriage-profiles/{profileId}/images/{imageId}
   */
  async deleteImage(profileId: string | number, imageId: string | number, changeReason?: string) {
    const token = GlobalUtils.getToken();

    const headers: Record<string, string> = {
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
    };
    if (changeReason) {
      headers["X-CHANGE-REASON"] = changeReason;
    }

    const res = await axios.delete(
      `${BASE_URL}/marriage-profiles/${encodeURIComponent(String(profileId))}/images/${encodeURIComponent(String(imageId))}`,
      { headers },
    );
    return res;
  },

  /**
   * Fetch audit / change-history logs for a marriage profile.
   * GET /api/marriage-profiles/{profileId}/audit
   */
  async getAuditLogs(profileId: string | number) {
    const token = GlobalUtils.getToken();
    const res = await axios.get(
      `${BASE_URL}/api/marriage-profiles/${encodeURIComponent(String(profileId))}/audit`,
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    return res.data;
  },
};
